import * as React from 'react';

import { StatsigProvider, useGateValue, useLayer } from '@coursera-web/statsig';

import NotFound from 'bundles/phoenix/components/NotFound';

/**
 * Route-level gate: renders children only when org-scoped home OR unified enterprise search is enabled.
 * Otherwise renders NotFound. Includes its own StatsigProvider since it renders at a route level
 * without a guaranteed provider ancestor.
 *
 * Uses useGateValue directly (not useIsOrgScopedHome) because this component lacks
 * EnterpriseProductFeaturesContext. The coarse Statsig gate is sufficient here — if a user
 * reached /organizations/:orgSlug/search, the per-org product feature was already validated
 * by the redirect flow upstream.
 */
const UnifiedEnterpriseGateInner = ({ children }: { children: React.ReactNode }) => {
  const isOrgScopedHomeEnabled = useGateValue('org-scoped-home', { disableExposureLog: true });
  const showUnifiedSearch = useLayer('enterprise-search-layer').get('unifiedEnterpriseSearch', false);

  if (!isOrgScopedHomeEnabled && !showUnifiedSearch) {
    return <NotFound />;
  }

  return <>{children}</>;
};

const UnifiedEnterpriseGate = ({ children }: { children: React.ReactNode }) => (
  <StatsigProvider>
    <UnifiedEnterpriseGateInner>{children}</UnifiedEnterpriseGateInner>
  </StatsigProvider>
);

export default UnifiedEnterpriseGate;
